// =============================================================================
// FILE: components/fx/WarpDrive.tsx
// PURPOSE: R2 Dynamic Background FX — three interactive modes, all reacting to
//   mouse position and movement in real time.
//
//   STARS  — hyperspace warp. Vanishing point tracks mouse. Emmy activates full warp.
//   SNOW   — gentle snowfall. Wind direction follows mouse X movement + velocity.
//   CLOUDS — atmospheric drift. Mouse creates turbulence / repulsion.
//
//   Mode selector: floating pill, bottom-left, persisted in localStorage.
//   Activation (warp): window.dispatchEvent(new CustomEvent('r2:warp', { detail: { active: true } }))
// =============================================================================

import { useEffect, useRef, useState, useCallback, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { Snowflake, Sparkles, Cloud } from 'lucide-react';

type BgMode = 'stars' | 'snow' | 'clouds';

interface WarpDriveProps {
  isSettingsActive?: boolean;
  backgroundEffectsEnabled?: boolean;
}

// ── Star particle ────────────────────────────────────────────────────────────
interface Star { nx: number; ny: number; z: number; size: number; hue: number; }
const STAR_COUNT = 280;
function makeStar(startFar = false): Star {
  const a = Math.random() * Math.PI * 2;
  const r = Math.random() * 0.45 + 0.02;
  return { nx: Math.cos(a) * r, ny: Math.sin(a) * r,
           z: startFar ? Math.random() : 0.85 + Math.random() * 0.15,
           size: Math.random() * 1.8 + 0.6, hue: 170 + Math.random() * 38 };
}

// ── Snowflake ────────────────────────────────────────────────────────────────
interface Flake { x: number; y: number; size: number; speed: number;
                  wobble: number; wobbleSpd: number; wobbleAmp: number; opacity: number; }
const SNOW_COUNT = 170;
function makeFlake(w: number, randomY = false): Flake {
  return { x: Math.random() * w, y: randomY ? Math.random() * 900 : -20,
           size: Math.random() * 3 + 0.8, speed: Math.random() * 1.1 + 0.4,
           wobble: Math.random() * Math.PI * 2,
           wobbleSpd: (Math.random() - 0.5) * 0.04,
           wobbleAmp: Math.random() * 1.5 + 0.3, opacity: Math.random() * 0.55 + 0.25 };
}

// ── Cloud puff ───────────────────────────────────────────────────────────────
interface CloudPuff { x: number; y: number; rx: number; ry: number;
                      speed: number; opacity: number; hue: number; }
const CLOUD_COUNT = 14;
function makePuff(w: number, h: number, placed = false): CloudPuff {
  return { x: placed ? Math.random() * w : -(70 + Math.random() * 130),
           y: Math.random() * h,
           rx: 55 + Math.random() * 90, ry: 28 + Math.random() * 45,
           speed: 0.12 + Math.random() * 0.22,
           opacity: 0.04 + Math.random() * 0.07, hue: 195 + Math.random() * 35 };
}

// ─────────────────────────────────────────────────────────────────────────────

export function WarpDrive({ isSettingsActive = false, backgroundEffectsEnabled = true }: WarpDriveProps): JSX.Element {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const starsRef   = useRef<Star[]>(Array.from({ length: STAR_COUNT }, () => makeStar(true)));
  const speedRef   = useRef(0);
  const activeRef  = useRef(false);
  const flakesRef  = useRef<Flake[]>([]);
  const flakeInitRef = useRef(false);
  const cloudsRef  = useRef<CloudPuff[]>([]);
  const cloudInitRef = useRef(false);

  // Mouse tracking refs (no re-render)
  const mouseRef   = useRef({ x: 0.5, y: 0.5 });
  const lerpRef    = useRef({ x: 0.5, y: 0.5 });
  const prevRef    = useRef({ x: 0.5, y: 0.5 });
  const velRef     = useRef({ x: 0, y: 0 });

  const [bgMode, setBgMode] = useState<BgMode>(() => {
    try { return (localStorage.getItem('r2-bg-mode') as BgMode) ?? 'stars'; }
    catch { return 'stars'; }
  });
  const bgModeRef = useRef<BgMode>(bgMode);
  bgModeRef.current = bgMode;

  // Persist mode
  useEffect(() => {
    try { localStorage.setItem('r2-bg-mode', bgMode); } catch { /* noop */ }
  }, [bgMode]);

  // Mouse tracking (single global listener, never causes re-render)
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const nx = e.clientX / window.innerWidth;
      const ny = e.clientY / window.innerHeight;
      velRef.current  = { x: nx - prevRef.current.x, y: ny - prevRef.current.y };
      prevRef.current = { x: nx, y: ny };
      mouseRef.current = { x: nx, y: ny };
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Warp event listener
  useEffect(() => {
    const handler = (e: Event) => {
      activeRef.current = (e as CustomEvent<{ active: boolean }>).detail.active;
    };
    window.addEventListener('r2:warp', handler);
    return () => window.removeEventListener('r2:warp', handler);
  }, []);

  // Single persistent animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId = 0;
    let alive  = true;

    const drawStars = (w: number, h: number) => {
      const target = activeRef.current ? 0.018 : isSettingsActive ? 0 : 0.0028;
      speedRef.current += (target - speedRef.current) * 0.045;
      const speed = speedRef.current;

      const trail = activeRef.current ? 0.12 : 0.3;
      ctx.fillStyle = `rgba(6,11,20,${trail})`;
      ctx.fillRect(0, 0, w, h);

      // Vanishing point smoothly follows mouse
      const cx  = lerpRef.current.x * w;
      const cy  = lerpRef.current.y * h;
      const fov = Math.min(w, h) * 0.58;

      for (const star of starsRef.current) {
        star.z -= speed;
        if (star.z <= 0.001) { Object.assign(star, makeStar()); continue; }

        const sx = cx + (star.nx / star.z) * fov;
        const sy = cy + (star.ny / star.z) * fov;
        if (sx < -20 || sx > w + 20 || sy < -20 || sy > h + 20) { Object.assign(star, makeStar()); continue; }

        const near = Math.pow(1 - star.z, 1.6);
        const bri  = Math.min(1, near * 2.2);
        const dotR = Math.max(0.25, star.size * near * 2.2);

        if (speed > 0.003) {
          const pz = Math.min(1, star.z + speed * 5);
          const px = cx + (star.nx / pz) * fov;
          const py = cy + (star.ny / pz) * fov;
          const len = Math.hypot(sx - px, sy - py);
          if (len > 0.5) {
            const g = ctx.createLinearGradient(px, py, sx, sy);
            g.addColorStop(0, `hsla(${star.hue},90%,70%,0)`);
            g.addColorStop(1, `hsla(${star.hue},92%,82%,${bri * 0.7})`);
            ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(sx, sy);
            ctx.strokeStyle = g; ctx.lineWidth = dotR * 0.55; ctx.stroke();
          }
        }

        ctx.beginPath(); ctx.arc(sx, sy, dotR, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${star.hue},92%,88%,${bri})`; ctx.fill();

        if (near > 0.55) {
          const gr = ctx.createRadialGradient(sx, sy, 0, sx, sy, dotR * 4);
          gr.addColorStop(0, `hsla(${star.hue},90%,80%,${near * 0.25})`);
          gr.addColorStop(1, `hsla(${star.hue},90%,70%,0)`);
          ctx.beginPath(); ctx.arc(sx, sy, dotR * 4, 0, Math.PI * 2);
          ctx.fillStyle = gr; ctx.fill();
        }
      }
    };

    const drawSnow = (w: number, h: number) => {
      if (!flakeInitRef.current) {
        flakesRef.current = Array.from({ length: SNOW_COUNT }, () => makeFlake(w, true));
        flakeInitRef.current = true;
      }

      ctx.fillStyle = 'rgba(6,11,20,0.18)';
      ctx.fillRect(0, 0, w, h);

      // Wind: mouse X position sets drift direction; velocity adds gust
      const wind  = (mouseRef.current.x - 0.5) * 1.6;
      const gust  = velRef.current.x * 18;
      const mx    = lerpRef.current.x * w;

      for (const f of flakesRef.current) {
        f.wobble += f.wobbleSpd;
        f.x += Math.sin(f.wobble) * f.wobbleAmp + wind + gust * 0.08;
        f.y += f.speed;
        // Gentle attraction toward mouse X
        f.x += (mx - f.x) * 0.0006;
        // Wrap
        if (f.x < -10) f.x = w + 10;
        if (f.x > w + 10) f.x = -10;
        if (f.y > h + 20) { f.x = Math.random() * w; f.y = -20; }

        ctx.save();
        ctx.globalAlpha = f.opacity;
        ctx.shadowBlur = f.size * 2.5; ctx.shadowColor = '#a8d8ff';
        ctx.beginPath(); ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2);
        ctx.fillStyle = '#d0eaff'; ctx.fill();
        ctx.restore();
      }
    };

    const drawClouds = (w: number, h: number) => {
      if (!cloudInitRef.current) {
        cloudsRef.current = Array.from({ length: CLOUD_COUNT }, () => makePuff(w, h, true));
        cloudInitRef.current = true;
      }

      ctx.fillStyle = 'rgba(6,11,20,0.08)';
      ctx.fillRect(0, 0, w, h);

      const wind = (mouseRef.current.x - 0.5) * 0.35;
      const mx   = lerpRef.current.x * w;
      const my   = lerpRef.current.y * h;

      for (const p of cloudsRef.current) {
        p.x += p.speed + wind;
        // Mouse repulsion
        const dx = p.x - mx, dy = p.y - my;
        const dist = Math.hypot(dx, dy);
        if (dist < 190) {
          const f = (190 - dist) / 190;
          p.x += (dx / dist) * f * 0.45;
          p.y += (dy / dist) * f * 0.2;
        }
        if (p.x - p.rx > w + 60) { p.x = -p.rx - 60; p.y = Math.random() * h; }

        // 3-blob cloud
        for (let b = 0; b < 3; b++) {
          const bx = p.x + (b - 1) * p.rx * 0.6;
          const by = p.y + (b === 1 ? -p.ry * 0.3 : 0);
          const brx = p.rx * (b === 1 ? 1 : 0.72);
          const bry = p.ry * (b === 1 ? 1 : 0.75);
          const g = ctx.createRadialGradient(bx, by, 0, bx, by, brx);
          g.addColorStop(0, `hsla(${p.hue},25%,68%,${p.opacity * 1.2})`);
          g.addColorStop(0.5, `hsla(${p.hue},20%,55%,${p.opacity * 0.45})`);
          g.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.beginPath(); ctx.ellipse(bx, by, brx, bry, 0, 0, Math.PI * 2);
          ctx.fillStyle = g; ctx.fill();
        }
      }
    };

    const draw = () => {
      if (!alive) return;
      const w = canvas.offsetWidth, h = canvas.offsetHeight;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = Math.max(w, 1); canvas.height = Math.max(h, 1);
      }
      // Smooth mouse lerp (separate from mousemove event rate)
      lerpRef.current.x += (mouseRef.current.x - lerpRef.current.x) * 0.028;
      lerpRef.current.y += (mouseRef.current.y - lerpRef.current.y) * 0.028;

      const mode = bgModeRef.current;
      if (mode === 'stars' || activeRef.current) drawStars(w, h);
      else if (mode === 'snow') drawSnow(w, h);
      else drawClouds(w, h);

      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);
    return () => { alive = false; cancelAnimationFrame(rafId); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSettingsActive]);

  const cycleMode = useCallback(() => {
    setBgMode(prev => prev === 'stars' ? 'snow' : prev === 'snow' ? 'clouds' : 'stars');
  }, []);

  if (!backgroundEffectsEnabled) return <></>;

  const MODES: { id: BgMode; icon: JSX.Element; label: string }[] = [
    { id: 'stars',  icon: <Sparkles size={11} />,  label: 'Stars'  },
    { id: 'snow',   icon: <Snowflake size={11} />, label: 'Snow'   },
    { id: 'clouds', icon: <Cloud size={11} />,     label: 'Clouds' },
  ];

  const selector = typeof document !== 'undefined' ? createPortal(
    <div
      data-testid="bg-mode-selector"
      style={{
        position: 'fixed', bottom: 34, left: 16, zIndex: 9200,
        display: 'flex', gap: 2, alignItems: 'center',
        background: 'rgba(6,10,18,0.82)',
        borderRadius: 20, padding: '3px 6px',
        border: '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(10px)',
      }}
    >
      {MODES.map(({ id, icon, label }) => (
        <button
          key={id}
          data-testid={`bg-mode-${id}`}
          onClick={() => setBgMode(id)}
          title={`${label} background (mouse-reactive)`}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '3px 8px', borderRadius: 14, border: 'none', cursor: 'pointer',
            background: bgMode === id ? 'rgba(94,234,212,0.16)' : 'transparent',
            color: bgMode === id ? '#5eead4' : 'rgba(148,172,210,0.35)',
            fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
            transition: 'all 0.18s',
          }}
        >
          {icon}
          <span style={{ marginLeft: 2 }}>{label}</span>
        </button>
      ))}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="warp-drive-canvas"
        aria-hidden="true"
        onClick={cycleMode}
        style={{
          position: 'fixed', inset: 0, width: '100%', height: '100%',
          pointerEvents: 'none', zIndex: 0,
          mixBlendMode: 'screen',
          opacity: isSettingsActive ? 0.04 : (bgMode === 'snow' ? 0.75 : bgMode === 'clouds' ? 0.55 : 0.45),
          transition: 'opacity 0.7s ease',
          willChange: 'transform',
        }}
      />
      {selector}
    </>
  );
}
